import { registerSW } from 'virtual:pwa-register';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError } from './api/client';
import { App, router } from './app';
import './i18n';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Garages kill connectivity constantly: retry transport failures, never a
      // 4xx — those are answers, not hiccups.
      retry: (failureCount, error) => error instanceof ApiError && error.isOffline && failureCount < 3,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Cached lot data should paint immediately even when the network is gone.
      networkMode: 'offlineFirst',
    },
    mutations: { retry: 0, networkMode: 'online' },
  },
});

/**
 * Routes where an update-triggered reload is hostile: /pay sits between the
 * quote and the Stripe confirm/redirect, /pay/return between the provider
 * redirect and settlement. The idempotency key survives in sessionStorage, but
 * a reload there still tears down Stripe Elements / in-flight UI state — the
 * update must never auto-apply while either is the current route.
 */
const PAYMENT_ROUTES = new Set(['/pay', '/pay/return']);

// Reloading is safe only off the payment routes AND with no mutation awaiting
// a response — a reload mid-request (start, settle, quote…) loses the answer.
const safeToReload = () => !PAYMENT_ROUTES.has(window.location.pathname) && queryClient.isMutating() === 0;

/**
 * The QR-scanning audience never reads update toasts (#20), so a new deploy
 * applies itself. registerType stays 'prompt' — not 'autoUpdate' with
 * skipWaiting — precisely so *we* pick the reload moment: apply immediately
 * when the current route is safe, otherwise hold the waiting worker and apply
 * on the first navigation that lands on a safe route. skipWaiting would let
 * the browser swap workers (and vite-plugin-pwa reload) mid-payment.
 */
const updateSW = registerSW({
  onNeedRefresh() {
    if (safeToReload()) {
      void updateSW(true);
      return;
    }
    const unsubscribe = router.subscribe('onResolved', () => {
      if (!safeToReload()) return;
      unsubscribe();
      void updateSW(true);
    });
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
